import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Client, Release, Status } from '@/types';
import { formatDate } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface ReleasesListProps {
  releases: { client: Client; release: Release }[];
  isLoading: boolean;
  title: string;
  icon: React.ReactNode;
  status: Status;
}

// Status colors for badges (white bg with colored text)
const statusColors = {
  'released': 'bg-white text-[#10b981] border border-[#10b981]', // release-released
  'ready for release': 'bg-white text-[#3b82f6] border border-[#3b82f6]', // release-ready
  'not ready': 'bg-white text-[#f59e0b] border border-[#f59e0b]', // release-not-ready
  'on hold': 'bg-white text-[#ef4444] border border-[#ef4444]' // release-on-hold
};

// Header background colors for status sections
const statusHeaderColors = {
  'released': 'bg-[#10b981] text-white', // green for released
  'ready for release': 'bg-[#3b82f6] text-white', // blue for ready
  'not ready': 'bg-[#f59e0b] text-white', // yellow/orange for not ready
  'on hold': 'bg-[#ef4444] text-white' // red for on hold
};

// Border colors for status-based card styling
const statusBorderColors = {
  'released': 'border-[#10b981]', // green for released
  'ready for release': 'border-[#3b82f6]', // blue for ready
  'not ready': 'border-[#f59e0b]', // yellow/orange for not ready
  'on hold': 'border-[#ef4444]' // red for on hold
};

const ReleasesList: React.FC<ReleasesListProps> = ({ releases, isLoading, title, icon, status }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const filteredReleases = releases.filter(({ release }) => release.status === status);
  const displayReleases = isExpanded ? filteredReleases : filteredReleases.slice(0, 3);

  if (filteredReleases.length === 0) {
    return null;
  }

  return (
    <Card className={`mb-4 bg-white border-2 rounded-lg overflow-hidden ${statusBorderColors[status]}`}>
      <div className={`p-4 ${statusHeaderColors[status]}`}>
        <CardTitle className="flex items-center">
          {icon}
          <span className="ml-2">{title}</span>
          <Badge className={`ml-3 ${statusColors[status]}`}>
            {filteredReleases.length} {filteredReleases.length === 1 ? 'Release' : 'Releases'}
          </Badge>
        </CardTitle>
      </div>
      <CardContent className="p-0">
        {isLoading ? (
          <p className="text-center py-4 text-gray-500">Loading releases...</p>
        ) : (
          <>
            <div className="relative overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50/50">
                  <tr>
                    <th scope="col" className="px-4 py-3 w-[100px]">Version</th>
                    <th scope="col" className="px-4 py-3">Name</th>
                    <th scope="col" className="px-4 py-3 w-[200px]">Planned Release Date</th>
                  </tr>
                </thead>
                <tbody>
                  {displayReleases.map(({ client, release }) => (
                    <tr
                      key={client.id}
                      onClick={() => navigate(`/clients/${client.id}/releases/${release.id}`)}
                      className="bg-white/50 border-b hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 w-[100px]">v{release.version}</td>
                      <td className="px-4 py-3 font-medium">{client.name}</td>
                      <td className="px-4 py-3 w-[200px]">
                        {release.plannedReleaseDate ? (
                          formatDate(release.plannedReleaseDate)
                        ) : (
                          <span className="text-gray-400">Not scheduled</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredReleases.length > 3 && (
              <div 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                className={`
                  py-2 px-4 flex items-center justify-center gap-1 cursor-pointer
                  text-sm text-gray-500 hover:text-gray-900 hover:bg-gray-50/80
                  border-t transition-colors
                `}
              >
                {isExpanded ? (
                  <>
                    Show Less <ChevronUp className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    View All {filteredReleases.length} Releases <ChevronDown className="w-4 h-4" />
                  </>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ReleasesList; 